

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  TemplateRef,
  ViewEncapsulation
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MwcEmptyI18nInterface, MwcI18nService } from 'mwc/i18n';

const MwcEmptyDefaultImages = ['default', 'simple'] as const;
type MwcEmptyNotFoundImageType = typeof MwcEmptyDefaultImages[number] | null | string | TemplateRef<void>;

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  selector: 'mwc-empty',
  exportAs: 'mwcEmpty',
  template: `
    <div class="mwc-empty-image">
      <ng-container *ngIf="!isImageBuildIn">
        <ng-container *mwcStringTemplateOutlet="mwcNotFoundImage">
          <img [src]="mwcNotFoundImage" [alt]="isContentString ? mwcNotFoundContent : 'empty'" />
        </ng-container>
      </ng-container>
      <mwc-empty-default *ngIf="isImageBuildIn && mwcNotFoundImage !== 'simple'"></mwc-empty-default>
      <mwc-empty-simple *ngIf="isImageBuildIn && mwcNotFoundImage === 'simple'"></mwc-empty-simple>
    </div>
    <p class="mwc-empty-description" *ngIf="mwcNotFoundContent !== null">
      <ng-container *mwcStringTemplateOutlet="mwcNotFoundContent">
        {{ isContentString ? mwcNotFoundContent : locale['description'] }}
      </ng-container>
    </p>
    <div class="mwc-empty-footer" *ngIf="mwcNotFoundFooter">
      <ng-container *mwcStringTemplateOutlet="mwcNotFoundFooter">
        {{ mwcNotFoundFooter }}
      </ng-container>
    </div>
  `,
  host: {
    class: 'mwc-empty'
  }
})
export class MwcEmptyComponent implements OnChanges, OnInit, OnDestroy {
  @Input() mwcNotFoundImage: MwcEmptyNotFoundImageType = 'default';
  @Input() mwcNotFoundContent?: string | TemplateRef<void> | null;
  @Input() mwcNotFoundFooter?: string | TemplateRef<void>;

  isContentString = false;
  isImageBuildIn = true;
  locale!: MwcEmptyI18nInterface;

  private readonly destroy$ = new Subject<void>();

  constructor(private i18n: MwcI18nService, private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    const { mwcNotFoundContent, mwcNotFoundImage } = changes;

    if (mwcNotFoundContent) {
      const content = mwcNotFoundContent.currentValue;
      this.isContentString = typeof content === 'string';
    }

    if (mwcNotFoundImage) {
      const image = mwcNotFoundImage.currentValue || 'default';
      this.isImageBuildIn = MwcEmptyDefaultImages.findIndex(i => i === image) > -1;
    }
  }

  ngOnInit(): void {
    this.i18n.localeChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.locale = this.i18n.getLocaleData('Empty');
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
